import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, IconButton, Tooltip } from '@mui/material'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { useShowWithDelay } from '~/hooks'
import { isObject } from '~/utils'
import Dialog from './Dialog'
import Spinner from './Spinner'

const ConfirmableButton = (props) => {
  const {
    dialog: dialogProps = {},
    icon,
    label,
    loading,
    title,
    onConfirmed,
    ...rest
  } = props

  const { t } = useTranslation()

  const [showConfirmation, setShowConfirmation] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const spinnerVisible = useShowWithDelay(500, loading)

  const onClick = useCallback((e) => {
    e.stopPropagation()
    setShowConfirmation(true)
  }, [])

  const button = useMemo(() => {
    let button
    if (label) {
      button = (
        <Button
          disabled={loading}
          variant="contained"
          {...rest}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: '0.5em' }}
          onClick={onClick}
        >
          {label}
          {loading && spinnerVisible && <Spinner delay={0} width="1em" />}
        </Button>
      )
    } else {
      button = (
        <IconButton
          disabled={loading}
          sx={{
            color: dialogProps.error ? 'danger' : 'primary.main',
            padding: 0,
          }}
          {...rest}
          onClick={onClick}
        >
          {loading && spinnerVisible ? <Spinner delay={0} width="1em" /> : icon}
        </IconButton>
      )
    }
    return title && !loading ? (
      <Tooltip title={title}>{button}</Tooltip>
    ) : (
      button
    )
  }, [
    dialogProps.error,
    icon,
    label,
    loading,
    onClick,
    rest,
    spinnerVisible,
    title,
  ])

  const dialog = useMemo(() => {
    return _.merge(
      {
        maxWidth: 'xs',
      },
      dialogProps,
      {
        actions: [
          {
            loading: loading || confirming,
            variant: 'contained',
            ...(isObject(dialogProps.actions?.confirm)
              ? {
                  name: t('ui.confirm'),
                  ...dialogProps.actions.confirm,
                }
              : { name: dialogProps.actions?.confirm ?? t('ui.confirm') }),
            id: 'confirm',
          },
          {
            autoFocus: true,
            variant: 'outlined',
            ...(isObject(dialogProps.actions?.cancel)
              ? { name: t('ui.cancel'), ...dialogProps.actions.cancel }
              : { name: dialogProps.actions?.cancel ?? t('ui.cancel') }),
            id: 'cancel',
          },
        ],
      }
    )
  }, [confirming, dialogProps, loading, t])

  return (
    <>
      {button}
      <Dialog
        fullWidth
        {...dialog}
        open={showConfirmation}
        onClose={async (action) => {
          if (action === 'confirm') {
            const result = onConfirmed?.()
            if (typeof result?.then === 'function') {
              setConfirming(true)
              result
                .then((hide) => setShowConfirmation(!hide))
                .finally(() => setConfirming(false))
            } else {
              setShowConfirmation(false)
            }
            // hide = await onConfirmed()
          } else {
            setShowConfirmation(false)
          }
        }}
      />
    </>
  )
}

ConfirmableButton.propTypes = {
  dialog: function (props, propName, componentName) {
    const value = props[propName]
    if (props.onConfirmed) {
      PropTypes.checkPropTypes(
        {
          [propName]: PropTypes.shape({
            actions: PropTypes.shape({
              confirm: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.object,
              ]),
              cancel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
            }),
            body: PropTypes.oneOfType([PropTypes.string, PropTypes.func])
              .isRequired,
            title: PropTypes.string.isRequired,
          }),
        },
        { [propName]: value },
        propName,
        componentName
      )
    }
  },
  icon: PropTypes.element,
  label: PropTypes.string,
  loading: PropTypes.bool,
  title: PropTypes.string,
  onConfirmed: function (props, propName, componentName) {
    const value = props[propName]
    if (props.dialog) {
      PropTypes.checkPropTypes(
        { [propName]: PropTypes.func.isRequired },
        { [propName]: value },
        propName,
        componentName
      )
    }
  },
}

export default ConfirmableButton
